/**
 * Apply-engine wire types (Phase 65 / BLU-594 PR3b, frontend).
 *
 * The plan shapes are DERIVED from `RouterOutputs` (`getProductSyncJobPlan`) —
 * that procedure returns a real `SyncPlan`, not `Json`, so the frontend never
 * re-declares a backend shape and can't drift from it.
 *
 * The POLLED job row is different: its `stats` is `Json` on the wire
 * (recursive → TS2589 at the property-access site), so — exactly as
 * `useReconcile.ts` does — the poll narrows the row to a `WireJob` with
 * `stats: unknown` and `asJobStats` narrows it at runtime.
 */
import type { RouterOutputs } from '@/lib/trpc';
import { parseCoverageStuckVariants, type CoverageStuckVariant } from '@threadheads/shared';

// --- source refs (mirror the startProductSyncJob scope zod) ----------------
export type SourceRef =
  | { type: 'upload'; key: string }
  | { type: 'drive'; fileId: string }
  | { type: 'url'; url: string };

// --- job status ------------------------------------------------------------
/** Straight off the wire — a new backend status shows up as a compile error in every consumer (e.g. `JobStatusPill`'s exhaustive record). */
export type ProductSyncJobStatus = RouterOutputs['productSync']['getProductSyncJobPlan']['status'];

export function isActiveJobStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'planning' || status === 'awaiting_approval' || status === 'running';
}

export function isTerminalJobStatus(status: string | null | undefined): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/**
 * The POLLED row shape (2s while active). Deliberately carries NO `plan`/
 * `scope`: the backend's poll/list select is stats-only because a 500-product
 * plan is multi-MB JSONB (`jobs.dao.ts` → `ProductSyncJobRow`). The plan is a
 * separate, fetched-once read — see `useProductSyncJobPlan`.
 * `stats` stays `unknown` to sever the recursive `Json` chain (TS2589).
 */
export interface WireJob {
  id: string;
  status: ProductSyncJobStatus;
  stats: unknown;
  error: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

// --- plan (fetched once via getProductSyncJobPlan) -------------------------
/** Full payload of the non-polled plan read. */
export type JobPlanPayload = RouterOutputs['productSync']['getProductSyncJobPlan'];
export type SyncPlan = NonNullable<JobPlanPayload['plan']>;
export type PlanProduct = SyncPlan['products'][number];
export type PlanBlocker = SyncPlan['blockers'][number];
export type PlanVariant = PlanProduct['variants'][number];
export type PlanFile = PlanProduct['files'][number];
export type PlanConflict = PlanProduct['conflicts'][number];

// --- stats (execute.ts ExecuteJobStats, all optional mid-run) --------------
export interface JobStatError {
  productId: string;
  /** Catalogue title (`execute.ts` records it) — lead with this so the operator never has to read a Shopify GID. Absent on jobs that ran before it shipped. */
  title?: string;
  sku?: string;
  stage: string;
  message: string;
}

export interface JobStats {
  /** BLU-1522 S5: the plan had nothing to review, so the run started without the approval gate. */
  autoApproved?: boolean;
  productsDone?: number;
  filesUploaded?: number;
  lookupsUpserted?: number;
  metafieldsWritten?: number;
  tencentSkipped?: number;
  /** Phase 86 'By set' platen variant runs: SKUs that got their group's file. */
  platenVariantSkusPublished?: number;
  /** SKUs left alone because their platen variant group had no file (they keep any file they had). */
  platenVariantFilesMissing?: number;
  /** SKUs whose platen info metafields are not set up, so only the file was written. */
  platenInfoWarnings?: number;
  /** Round 4: SKUs that got a Small file made from Large. Absent on older jobs and jobs that made none. */
  platenSmallMade?: number;
  /** Round 4: SKUs whose Small file uploaded by hand was kept (nothing made for their group). Same presence rule. */
  platenSmallKept?: number;
  /**
   * Phase 92 (92-25), present ONLY on a job whose plan carried variants on a
   * 3PL's blank: blanks whose client alias or supply mapping this job created,
   * supply variants skipped (their reasons are in `errors`), and supply
   * products whose print file names were or were not updated after the run.
   */
  supplyAliasesEnsured?: number;
  supplyVariantsRefused?: number;
  supplyMetadataResynced?: number;
  supplyMetadataResyncPending?: number;
  /**
   * Phase 92 (92-25): plain notices that never fail the job. Present only when
   * there is one. `count` lets a counted notice (a coverage pass that left out
   * products changed after their stock approval) read in the reader's language.
   */
  warnings?: Array<{ productId: string; stage: string; message: string; count?: number }>;
  errors?: JobStatError[];
  note?: string;
  /** Optional durable inventory-cache validation outcome (newer jobs only). */
  inventoryCoverage?: unknown;
  /**
   * Phase 88, store SKU set-up jobs only (`declare-job.ts` DeclaredJobStats):
   * rows inserted, variants that changed in the store after review, variants
   * a parallel write had already set up, and variants never approved.
   * `alreadySetUp` (88-12, 88a.1): approved variants already set up with the
   * reviewed store SKU before the run (by the automatic set-up or an upload).
   */
  identity?: 'declared';
  declared?: number;
  changedSinceReview?: number;
  alreadySetUp?: number;
  skippedAtWrite?: number;
  skipped?: number;
}

export interface InventoryCoverageStats {
  status: 'ready' | 'pending';
  affectedBlankSkus?: number;
  pendingBlankSkus?: number;
  message?: string;
  /** False marks terminal amber: no automatic durable retry remains and the
   * operator must run Product Sync again. Missing preserves legacy auto-retry. */
  autoRetry?: boolean;
  /**
   * BLU-1519: the variants the coverage pass could not link yet, each with a
   * reason (at most 100 rows). Present only on a pass that linked every other
   * variant; absent on older jobs, which keep the blank-SKU wording.
   */
  stuckVariants?: CoverageStuckVariant[];
  /** BLU-1519: the full stuck total (the list may be capped). */
  stuckVariantCount?: number;
  /** BLU-1519: the approved variants the pass checked (stuck + linked). */
  approvedVariantCount?: number;
}

// --- runtime coercers ------------------------------------------------------
/**
 * Defensive read of a STORED plan. The wire type is honest, but the row is
 * JSONB written by older builds too, so a legacy doc missing `products` /
 * `totals` must degrade to null / zeroed totals rather than throw inside the
 * approval gate.
 */
export function asPlan(plan: SyncPlan | null | undefined): SyncPlan | null {
  if (!plan || typeof plan !== 'object') return null;
  const p: Partial<SyncPlan> = plan;
  if (!Array.isArray(p.products)) return null;
  return {
    // Phase 88: a declared plan says so, and the approval gate branches on it.
    // Absent (every plan stored before Phase 88) stays absent, read as derived.
    ...(p.identity ? { identity: p.identity } : {}),
    // 86-23 review: a store SKU org's plan runs only with every blocker fixed.
    ...(p.storeSkuByo === true ? { storeSkuByo: true as const } : {}),
    products: p.products,
    blockers: Array.isArray(p.blockers) ? p.blockers : [],
    totals: p.totals ?? { products: 0, blockedProducts: 0, variants: 0, files: 0, conflicts: 0 },
  };
}

export function asJobStats(stats: unknown): JobStats {
  if (!stats || typeof stats !== 'object') return {};
  return stats as JobStats;
}

/** Backward-compatible coercion for jobs created before inventory validation. */
export function asInventoryCoverage(value: unknown): InventoryCoverageStats | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.status !== 'ready' && raw.status !== 'pending') return null;
  const nonNegativeNumber = (candidate: unknown): number | undefined =>
    typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0
      ? candidate
      : undefined;
  return {
    status: raw.status,
    affectedBlankSkus: nonNegativeNumber(raw.affectedBlankSkus),
    pendingBlankSkus: nonNegativeNumber(raw.pendingBlankSkus),
    message: typeof raw.message === 'string' && raw.message.trim() ? raw.message : undefined,
    autoRetry: typeof raw.autoRetry === 'boolean' ? raw.autoRetry : undefined,
    ...(Array.isArray(raw.stuckVariants)
      ? { stuckVariants: parseCoverageStuckVariants(raw.stuckVariants) }
      : {}),
    stuckVariantCount: nonNegativeNumber(raw.stuckVariantCount),
    approvedVariantCount: nonNegativeNumber(raw.approvedVariantCount),
  };
}

/**
 * BLU-1519: a completed job whose coverage is waiting on a person to fix
 * variants in Shopify. It still polls (the hourly retry or a Check again can
 * turn it green), but slowly: the stats now carry the stuck list.
 */
export function isWaitingOnStuckVariants(status: string | null | undefined, stats: unknown): boolean {
  if (status !== 'completed') return false;
  const coverage = asInventoryCoverage(asJobStats(stats).inventoryCoverage);
  return coverage?.status === 'pending' && (coverage.stuckVariantCount ?? 0) > 0;
}

/** Whether the post-apply inventory validation is still amber, including a
 * terminal amber state that requires another Product Sync run. */
export function hasPendingInventoryCoverage(stats: unknown): boolean {
  return asInventoryCoverage(asJobStats(stats).inventoryCoverage)?.status === 'pending';
}

// --- supply metadata resync wait (Phase 92, 92-25 review m2) --------------
/**
 * Mirror of the backend's per-org sweep lease TTL, `SWEEP_LEASE_TTL_MS` in
 * `apps/backend/src/modules/shopify/metafield-mappings/sync-state.types.ts`
 * (30 minutes). KEEP IN LOCKSTEP: the frontend cannot import backend modules.
 */
export const SWEEP_LEASE_TTL_MS = 30 * 60 * 1000;

/**
 * How long after `finished_at` a completed job's detached supply metadata
 * resync can still patch its stats: it waits up to the lease TTL + 60 s for
 * the org's sweep lease (`RESYNC_LEASE_MAX_WAIT_MS` in
 * `apps/backend/src/modules/shopify/metafield-mappings/product-metadata-sweep.ts`).
 */
export const SUPPLY_RESYNC_WAIT_MS = SWEEP_LEASE_TTL_MS + 60_000;

/** Today's poll for an active job (and a completed job's pending coverage retry). */
export const JOB_POLL_MS = 2_000;
/** BLU-1519: a job waiting on stuck variants (a person fixing them in Shopify). */
export const STUCK_VARIANTS_POLL_MS = 15_000;

/**
 * Mirror of `RESYNC_LEASE_POLL_MS` (product-metadata-sweep.ts): a resync that
 * found the lease held retries only this often, so a faster poll cannot see
 * its answer sooner. Used after the first minute of the wait.
 */
export const SUPPLY_RESYNC_SLOW_POLL_MS = 10_000;

/** The first minute after finish keeps the 2 s poll: an uncontended resync answers inside it. */
export const SUPPLY_RESYNC_FAST_WINDOW_MS = 60_000;

function hasSupplyResyncWarning(stats: JobStats): boolean {
  return (
    Array.isArray(stats.warnings) &&
    stats.warnings.some((w) => w != null && typeof w === 'object' && w.stage === 'supply_metadata_resync')
  );
}

/**
 * The moment a completed job's detached supply resync is given up on:
 * `finished_at` + `SUPPLY_RESYNC_WAIT_MS`. Null when the job is not waiting
 * on one: not completed, nothing pending, the resync already reported a
 * shortfall (its `supply_metadata_resync` warning), or no readable
 * `finished_at` (never an unbounded wait). A full success patches the pending
 * count to 0, which ends the wait too.
 *
 * The bound compares the server's `finished_at` with the browser clock, so a
 * skewed clock moves it by the skew: still bounded, and a clock that runs
 * ahead only shows today's line sooner.
 */
export function supplyResyncDeadline(
  status: string | null | undefined,
  stats: unknown,
  finishedAt: string | null | undefined,
): number | null {
  if (status !== 'completed') return null;
  const s = asJobStats(stats);
  if (statNum(s, 'supplyMetadataResyncPending') <= 0) return null;
  if (hasSupplyResyncWarning(s)) return null;
  const finishedMs = finishedAt ? Date.parse(finishedAt) : Number.NaN;
  if (!Number.isFinite(finishedMs)) return null;
  return finishedMs + SUPPLY_RESYNC_WAIT_MS;
}

/** True while that resync can still answer: the report says "Updating print file names...", not the warning. */
export function isAwaitingSupplyResync(
  status: string | null | undefined,
  stats: unknown,
  finishedAt: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  const deadline = supplyResyncDeadline(status, stats, finishedAt);
  return deadline !== null && nowMs <= deadline;
}

/**
 * Active jobs always poll. A completed job is the intentional terminal
 * exception while its durable inventory-coverage retry can still replace
 * amber stats with ready stats, or (given its `finishedAt`) while its
 * detached supply resync can still report (`isAwaitingSupplyResync`).
 * Terminal amber (`autoRetry: false`) and failed/cancelled jobs do not poll.
 * Without `finishedAt` the answer is exactly the pre-92 rule.
 */
export function shouldPollProductSyncJob(
  status: string | null | undefined,
  stats: unknown,
  finishedAt?: string | null,
  nowMs: number = Date.now(),
): boolean {
  const coverage = asInventoryCoverage(asJobStats(stats).inventoryCoverage);
  return isActiveJobStatus(status)
    || (status === 'completed'
      && coverage?.status === 'pending'
      && coverage.autoRetry !== false)
    || isAwaitingSupplyResync(status, stats, finishedAt, nowMs);
}

/**
 * The polled job's refetch interval. Every job that polled before Phase 92
 * polls exactly as before (2 s while `shouldPollProductSyncJob` holds without
 * the resync wait, then stops). A completed job that is ONLY waiting on its
 * supply resync polls 2 s for the first minute, then 10 s, and stops at the
 * bound: at most about 210 small stats reads in the worst case (another
 * sweep holding the lease), against about 930 at a flat 2 s.
 */
export function productSyncJobPollMs(
  job: Pick<WireJob, 'status' | 'stats' | 'finished_at'>,
  nowMs: number = Date.now(),
): number | false {
  if (shouldPollProductSyncJob(job.status, job.stats)) {
    return isWaitingOnStuckVariants(job.status, job.stats) ? STUCK_VARIANTS_POLL_MS : JOB_POLL_MS;
  }
  const deadline = supplyResyncDeadline(job.status, job.stats, job.finished_at);
  if (deadline === null || nowMs > deadline) return false;
  const sinceFinishMs = nowMs - (deadline - SUPPLY_RESYNC_WAIT_MS);
  return sinceFinishMs < SUPPLY_RESYNC_FAST_WINDOW_MS ? JOB_POLL_MS : SUPPLY_RESYNC_SLOW_POLL_MS;
}

export function statNum(stats: JobStats, key: keyof JobStats): number {
  const v = stats[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
